import React, { Fragment, useContext, useRef, useState } from 'react';
import SupplierFilter from '../../components/SupplierFilter/SupplierFilter';
import { LoginContext } from '../../contexts/LoginContext';
import ViewPortGridWrapper from '../../containers/ViewPortGridWrapper';
import { Button, makeStyles, TextField } from '@material-ui/core';
import CustomDatePicker from '../../components/UI/CustomDatePicker';
import ServicePagesGrid from './ServicePageGrid';
import { commonFilter } from '../../constants/StyleConstants';
import servicePageAPI from '../../services/ServicePageAPI';
import { AppContext } from '../../contexts/AppContext';
import { todayDate, todayLastYear, YMD_FORMAT } from '../../utils/DateUtils';
import DefaultUnrederedGridMessage from '../../containers/DefaultUnrenderedGridMessage';

const useStyles = makeStyles(theme => {
  return {
    ...commonFilter,
  };
});

const Filters = ({
  isSupplier,
  search,
  selectedSupplier,
  setSelectedSupplier,
  number,
  setNumber,
  dateTo,
  setDateTo,
  dateFrom,
  setDateFrom,
  serviceSheetNumber,
  setServiceSheetNumber,
}) => {
  const classes = useStyles();

  const cleanFilters = () => {
    setSelectedSupplier(null);
    setNumber('');
    setDateFrom(null);
    setDateTo(null);
    setServiceSheetNumber('');
  };

  return (
    <div className={classes.filters}>
      {!isSupplier && <SupplierFilter narrow value={selectedSupplier} setValue={setSelectedSupplier} />}
      <TextField
        style={{ width: 180 }}
        margin="dense"
        id="serviceSheetNumber"
        label="Nro. de hoja de servicio"
        onChange={e => setServiceSheetNumber(e.target.value)}
        value={serviceSheetNumber}
      />
      <TextField
        style={{ width: 200 }}
        margin="dense"
        id="razonSocial"
        label="Nro. de pedido de compra"
        onChange={e => setNumber(e.target.value)}
        value={number}
      />
      <CustomDatePicker value={dateFrom} maxDate={dateTo} label="Fecha desde" setValue={setDateFrom} />
      <CustomDatePicker value={dateTo} minDate={dateFrom} label="Fecha hasta" setValue={setDateTo} />
      <div className={classes.filtersActionButton}>
        <Button variant="outlined" color="primary" onClick={search}>
          Filtrar
        </Button>
        <Button variant="outlined" color="secondary" onClick={cleanFilters}>
          Limpiar
        </Button>
      </div>
    </div>
  );
};

const ServicePage = ({ fullResponsive, onRowClick }) => {
  const { isSupplier, idSupplier } = useContext(LoginContext);
  const { showError, showWarning } = useContext(AppContext);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [number, setNumber] = useState('');
  const [dateTo, setDateTo] = useState(null);
  const [dateFrom, setDateFrom] = useState(null);
  const [serviceSheetNumber, setServiceSheetNumber] = useState('');

  const [triggerNewSearch, setTriggerNewSearch] = useState(true);
  const [oldSearchRequest, setOldSearchRequest] = useState({});

  const [firstLoad, setFirstLoad] = useState(true);
  const tableRef = useRef();

  const search = (selectedSupplier, dateTo, dateFrom, purchaseOrderNumber, servceSheetNumber, page, pageSize) => {
    const request = triggerNewSearch
      ? {
          Cuit: isSupplier ? idSupplier : selectedSupplier.cuit,
          StartDate: dateFrom ? dateFrom.format(YMD_FORMAT) : todayLastYear(),
          EndDate: dateTo ? dateTo.format(YMD_FORMAT) : todayDate(),
          PurchaseOrder: !purchaseOrderNumber.isBlank() ? purchaseOrderNumber : undefined,
          ServiceRecord: !servceSheetNumber.isBlank() ? servceSheetNumber : undefined,
          PageNumber: page,
          PageSize: pageSize,
        }
      : {
          ...oldSearchRequest,
          PageNumber: page,
          PageSize: pageSize,
        };

    return servicePageAPI
      .getServicePage(request)
      .then(res => res)
      .catch(error => {
        const errorMsg = 'Error al buscar las hojas de servicio';
        showError(errorMsg, error);
        throw new Error(errorMsg);
      })
      .finally(() => {
        if (triggerNewSearch) {
          setOldSearchRequest(request);
          setTriggerNewSearch(false);
        }
      });
  };

  const validateFilters = () => {
    let errors = [];
    const errorMessages = [];
    let i = 0;

    if ((errors[i++] = !isSupplier && !selectedSupplier)) {
      errorMessages.push('Debe seleccionar un proveedor para iniciar la búsqueda');
    }

    if ((errors[i++] = dateFrom && !dateTo)) {
      errorMessages.push('Debe completar el rango de fechas o quitar la Fecha desde');
    }

    if ((errors[i++] = !dateFrom && dateTo)) {
      errorMessages.push('Debe completar el rango de fechas o quitar la Fecha hasta');
    }

    const hasErrors = errors.some(e => e);

    hasErrors && showWarning(errorMessages);

    return !hasErrors;
  };

  const validateAndSearch = () => {
    if (validateFilters()) {
      if (firstLoad) {
        setFirstLoad(false);
      } else {
        setTriggerNewSearch(true);
        tableRef.current.onChangePage(null, 0);
      }
    }
  };

  return (
    <Fragment>
      <ViewPortGridWrapper fullResponsive={fullResponsive}>
        <Filters
          isSupplier={isSupplier}
          search={validateAndSearch}
          selectedSupplier={selectedSupplier}
          setSelectedSupplier={setSelectedSupplier}
          number={number}
          setNumber={setNumber}
          dateTo={dateTo}
          setDateTo={setDateTo}
          dateFrom={dateFrom}
          setDateFrom={setDateFrom}
          serviceSheetNumber={serviceSheetNumber}
          setServiceSheetNumber={setServiceSheetNumber}
        />
        <DefaultUnrederedGridMessage render={isSupplier || !firstLoad}>
          <ServicePagesGrid
            data={query =>
              new Promise((resolve, reject) => {
                search(selectedSupplier, dateTo, dateFrom, number, serviceSheetNumber, query.page + 1, query.pageSize)
                  .then(result =>
                    resolve({
                      data: result.data,
                      page: query.page,
                      totalCount: result.totalCount,
                    })
                  )
                  .catch(error => reject(error));
              })
            }
            onRowClick={onRowClick}
            tableRef={tableRef}
          />
        </DefaultUnrederedGridMessage>
      </ViewPortGridWrapper>
    </Fragment>
  );
};

export default ServicePage;
